import { z } from "zod";
import { conversionTargetSchema, operationSchema } from "./job-request";
import { jobStateSchema } from "./job-state";

export const documentJobOutputSchema = z.object({
  assetId: z.string().min(1),
  position: z.number().int().nonnegative(),
  checksumSha256: z.string().regex(/^[a-f0-9]{64}$/),
  bytes: z.number().int().nonnegative(),
  mimeType: z.string().min(1),
});

export const documentJobSchema = z.object({
  id: z.string().min(1),
  ownerId: z.string().min(1),
  operation: operationSchema,
  status: jobStateSchema,
  queueClass: z.enum(["light", "heavy", "ocr"]),
  progress: z.number().int().min(0).max(100),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
  errorCode: z.string().min(1).nullable().optional(),
  outputs: z.array(documentJobOutputSchema).optional(),
});

export const documentJobResponseSchema = z.object({ job: documentJobSchema });
export const documentJobsResponseSchema = z.object({ jobs: z.array(documentJobSchema) });

export const documentCapabilitySchema = z.object({
  operation: operationSchema,
  inputMimeTypes: z.array(z.string().min(1)),
  outputMimeTypes: z.array(z.string().min(1)),
  conversionPairs: z.array(z.object({
    inputMimeTypes: z.array(z.string().min(1)).min(1),
    target: conversionTargetSchema,
    outputMimeType: z.string().min(1),
    executionModes: z.array(z.enum(["local", "server"])).min(1),
  })).optional(),
  executionModes: z.array(z.enum(["local", "server"])),
  authenticationRequiredForServer: z.boolean(),
  enabled: z.boolean(),
  limits: z.object({ maxBytes: z.number().int().positive(), maxPages: z.number().int().positive() }),
  unavailableReason: z.string().min(1).optional(),
});
export const documentCapabilitiesResponseSchema = z.object({ capabilities: z.array(documentCapabilitySchema) });

export const assetReferenceSchema = z.object({
  assetId: z.string().min(1).optional(),
  id: z.string().min(1).optional(),
}).passthrough().refine((asset) => Boolean(asset.assetId || asset.id), "Asset ID is required.");

export const uploadSessionResponseSchema = z.object({
  uploadSessionId: z.string().min(1).optional(),
  uploadUrl: z.url().optional(),
  expiresAt: z.string().min(1).optional(),
  requiredHeaders: z.record(z.string(), z.string()).default({}),
  deduplicated: z.boolean().optional(),
  asset: assetReferenceSchema.optional(),
}).refine((response) => Boolean(response.asset || (response.uploadSessionId && response.uploadUrl)), {
  message: "A reusable asset or complete upload session is required.",
});

export const finalizeUploadResponseSchema = z.object({ asset: assetReferenceSchema });
export const downloadUrlResponseSchema = z.object({ downloadUrl: z.url(), expiresAt: z.string().min(1).optional() });

export const documentAssetSchema = z.object({
  assetId: z.string().min(1),
  variant: z.string().min(1),
  mime: z.string().min(1),
  bytes: z.number().int().nonnegative(),
  status: z.enum(["temporary", "active", "trashed", "archived"]),
  filename: z.string().min(1).nullable(),
  createdAt: z.string().min(1),
  previewUrl: z.url().optional(),
  saved: z.boolean().optional(),
}).passthrough();

export const documentFilesResponseSchema = z.object({
  assets: z.array(documentAssetSchema),
  nextCursor: z.string().min(1).nullable().optional(),
});

export const documentUsageSchema = z.object({
  jobsToday: z.number().int().nonnegative(),
  bytesToday: z.number().int().nonnegative(),
  concurrentJobs: z.number().int().nonnegative(),
});

export const documentUsageResponseSchema = z.object({ usage: documentUsageSchema });

export const documentErrorResponseSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    requestId: z.string().min(1),
    retryable: z.boolean(),
  }),
});

export type DocumentJob = z.infer<typeof documentJobSchema>;
export type DocumentJobOutput = z.infer<typeof documentJobOutputSchema>;
export type DocumentCapability = z.infer<typeof documentCapabilitySchema>;
export type DocumentAsset = z.infer<typeof documentAssetSchema>;
export type DocumentUsage = z.infer<typeof documentUsageSchema>;
